// Resolução das imagens de cards na importação de decks/subdecks.
//
// `media.images[].url` pode vir de três formas no arquivo importado:
//   - http(s)://...  → mantida como está
//   - data:...       → rejeitada (base64 infla o documento do deck, que tem limite de 16 MB no MongoDB)
//   - caminho relativo (ex.: img/foto.webp) → arquivo dentro do .zip, enviado ao Cloudinary
//
// As funções deste arquivo são puras (sem rede/DOM) para poderem ser testadas isoladamente;
// a leitura do zip e o upload ficam em exportImport.service.ts.

export class ImportImageError extends Error {
    constructor(message: string) {
        super(message);
        this.name = 'ImportImageError';
    }
}

export interface ImageRef {
    path: string;        // caminho relativo já normalizado
    subdeckName: string; // usado como pasta no Cloudinary (primeiro subdeck em que aparece)
}

export type ImageUrlKind = 'remote' | 'data' | 'relative';

export const classifyImageUrl = (url: string): ImageUrlKind => {
    const trimmed = (url || '').trim();
    if (/^https?:\/\//i.test(trimmed)) return 'remote';
    if (/^data:/i.test(trimmed)) return 'data';
    return 'relative';
};

export const normalizeImagePath = (url: string): string => {
    let path = (url || '').trim().replace(/\\/g, '/');
    while (path.startsWith('./')) path = path.slice(2);
    return path;
};

const DATA_URL_MESSAGE =
    'Imagens em base64 (data:...) não são aceitas: elas estouram o limite de 16 MB do documento do deck no MongoDB. ' +
    'Exporte as imagens como arquivos e importe um .zip com o JSON + as imagens.';

// Percorre todas as listas de cards do arquivo, nos três formatos aceitos:
//   - deck: data.subdecks[].cards[]
//   - deck legado: data.cards[]
//   - arquivo de subdeck (importSubdeck): data.cards[]
const forEachCardList = (data: any, fn: (cards: any[], subdeckName: string) => void) => {
    if (Array.isArray(data?.subdecks)) {
        for (const subdeck of data.subdecks) {
            if (Array.isArray(subdeck?.cards)) fn(subdeck.cards, subdeck.name || data.name || '');
        }
    }
    if (Array.isArray(data?.cards)) fn(data.cards, data.name || '');
};

/**
 * Lista os caminhos relativos únicos usados em media.images (sem duplicatas).
 * Lança ImportImageError se houver alguma imagem em base64.
 */
export const collectImageRefs = (data: any): ImageRef[] => {
    const refs = new Map<string, ImageRef>();
    let dataUrlCount = 0;

    forEachCardList(data, (cards, subdeckName) => {
        for (const card of cards) {
            const images = card?.media?.images;
            if (!Array.isArray(images)) continue;
            for (const image of images) {
                if (!image || typeof image.url !== 'string') continue;
                const kind = classifyImageUrl(image.url);
                if (kind === 'data') {
                    dataUrlCount++;
                } else if (kind === 'relative') {
                    const path = normalizeImagePath(image.url);
                    if (path && !refs.has(path)) refs.set(path, { path, subdeckName });
                }
            }
        }
    });

    if (dataUrlCount > 0) {
        throw new ImportImageError(`${dataUrlCount} imagem(ns) em base64 encontrada(s). ${DATA_URL_MESSAGE}`);
    }

    return Array.from(refs.values());
};

/**
 * Devolve uma cópia do arquivo importado com cada URL relativa de media.images
 * trocada pela URL do mapa (caminho normalizado → URL). URLs http(s) ficam como estão.
 * Lança ImportImageError para base64 ou para caminhos ausentes do mapa (lista todos).
 */
export const rewriteImageUrls = <T>(data: T, urlMap: Record<string, string>): T => {
    const copy = JSON.parse(JSON.stringify(data));
    const missing = new Set<string>();
    let dataUrlCount = 0;

    forEachCardList(copy, (cards) => {
        for (const card of cards) {
            const images = card?.media?.images;
            if (!Array.isArray(images)) continue;
            for (const image of images) {
                if (!image || typeof image.url !== 'string') continue;
                const kind = classifyImageUrl(image.url);
                if (kind === 'data') {
                    dataUrlCount++;
                } else if (kind === 'relative') {
                    const path = normalizeImagePath(image.url);
                    const resolved = urlMap[path];
                    if (resolved) image.url = resolved;
                    else missing.add(path);
                }
            }
        }
    });

    if (dataUrlCount > 0) {
        throw new ImportImageError(`${dataUrlCount} imagem(ns) em base64 encontrada(s). ${DATA_URL_MESSAGE}`);
    }
    if (missing.size > 0) {
        throw new ImportImageError(`Imagem(ns) não encontrada(s): ${Array.from(missing).join(', ')}`);
    }

    return copy;
};

// Mesmo limite e tipos aceitos pela rota POST /api/upload/card-image-cloudinary (uploadRoutes.js)
export const MAX_IMAGE_BYTES = 10 * 1024 * 1024;

export const IMAGE_MIME_BY_EXT: Record<string, string> = {
    png: 'image/png',
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    svg: 'image/svg+xml',
    webp: 'image/webp',
    gif: 'image/gif'
};

export const imageMimeFromPath = (path: string): string | null => {
    const ext = path.split('.').pop()?.toLowerCase() || '';
    return IMAGE_MIME_BY_EXT[ext] || null;
};
